/**
 * `/notes` — free-text tank journal (Phase 2B).
 *
 * A simple, timestamped journal for the operator to record observations,
 * maintenance notes, livestock additions, dosing tweaks, etc. Entries are
 * stored server-first via the `journal` UserDataStore namespace
 * (`useServerBackedState`), so they survive a browser clear, a device
 * switch, and `pi-deploy`. Newest entries render first. Each entry can be
 * tagged with a category and deleted; there is no edit-in-place — the
 * journal is intentionally append-only-ish (delete + re-add to correct).
 */
import { useMemo, useState } from 'react';

import { Card, CardBody, CardHeader, CardTitle } from '../components/Card';
import { useServerBackedState } from '../hooks/useServerBackedState';

/** A single journal entry. */
export interface JournalEntry {
  readonly id: string;
  readonly body: string;
  readonly category: JournalCategory;
  readonly createdAt: string; // ISO timestamp
}

/** Stored blob for the `journal` namespace. */
export interface JournalData {
  readonly entries: readonly JournalEntry[];
}

export const EMPTY_JOURNAL: JournalData = { entries: [] };

export type JournalCategory =
  | 'observation'
  | 'maintenance'
  | 'livestock'
  | 'dosing'
  | 'equipment'
  | 'other';

const CATEGORY_LABELS: ReadonlyArray<{ value: JournalCategory; label: string }> = [
  { value: 'observation', label: 'Observation' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'livestock', label: 'Livestock' },
  { value: 'dosing', label: 'Dosing' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'other', label: 'Other' },
];

function labelFor(category: JournalCategory): string {
  return CATEGORY_LABELS.find((c) => c.value === category)?.label ?? 'Other';
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function NotesPage() {
  const { value: data, setValue, loading } =
    useServerBackedState<JournalData>('journal', EMPTY_JOURNAL);

  const [draftBody, setDraftBody] = useState('');
  const [draftCategory, setDraftCategory] = useState<JournalCategory>('observation');

  const entries = data.entries;

  // Newest first — copy before sorting so we never mutate the stored array.
  const sortedEntries = useMemo(
    () =>
      [...entries].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [entries],
  );

  const persist = (next: readonly JournalEntry[]): void => {
    setValue({ entries: next });
  };

  const addEntry = (): void => {
    const body = draftBody.trim();
    if (body.length === 0) return;
    const entry: JournalEntry = {
      id: crypto.randomUUID(),
      body,
      category: draftCategory,
      createdAt: new Date().toISOString(),
    };
    persist([...entries, entry]);
    setDraftBody('');
    setDraftCategory('observation');
  };

  const deleteEntry = (id: string): void => {
    persist(entries.filter((e) => e.id !== id));
  };

  return (
    <div className="min-h-screen px-6 py-8" data-testid="notes-page">
      <header className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-semibold tracking-tight">Tank Journal</h1>
        <span className="text-xs text-reef-400" data-testid="notes-count">
          {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
        </span>
      </header>

      <div className="grid gap-6 lg:grid-cols-[2fr_3fr]">
        {/* Add-entry composer */}
        <Card>
          <CardHeader>
            <CardTitle>New entry</CardTitle>
          </CardHeader>
          <CardBody className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-xs text-reef-300">
              Category
              <select
                className="rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100"
                value={draftCategory}
                onChange={(ev) =>
                  setDraftCategory(ev.target.value as JournalCategory)
                }
                data-testid="notes-category"
              >
                {CATEGORY_LABELS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-xs text-reef-300">
              Note
              <textarea
                className="min-h-28 resize-y rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100"
                value={draftBody}
                onChange={(ev) => setDraftBody(ev.target.value)}
                data-testid="notes-body"
                placeholder="Added 2 frags of green slimer; tightened return pump union; nitrate looking high…"
              />
            </label>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={addEntry}
                disabled={draftBody.trim().length === 0}
                className="reefapp-btn-secondary text-sm disabled:opacity-50"
                data-testid="notes-add"
              >
                Add entry
              </button>
            </div>
          </CardBody>
        </Card>

        {/* Journal feed */}
        <Card>
          <CardHeader>
            <CardTitle>Journal</CardTitle>
          </CardHeader>
          <CardBody>
            {loading ? (
              <p className="text-sm text-reef-400">Loading…</p>
            ) : sortedEntries.length === 0 ? (
              <p className="text-sm text-reef-400" data-testid="notes-empty">
                No journal entries yet. Record an observation, a maintenance
                action, or a livestock addition to start your tank history.
              </p>
            ) : (
              <ul className="flex flex-col gap-3" data-testid="notes-list">
                {sortedEntries.map((entry) => (
                  <li
                    key={entry.id}
                    className="rounded-md border border-reef-700 bg-reef-950/50 px-3 py-2"
                    data-testid={`notes-entry-${entry.id}`}
                  >
                    <div className="mb-1 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="rounded bg-reef-700 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-reef-200">
                          {labelFor(entry.category)}
                        </span>
                        <span className="text-xs text-reef-400">
                          {formatTimestamp(entry.createdAt)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteEntry(entry.id)}
                        className="text-xs text-coral-400 hover:text-coral-500"
                        data-testid={`notes-delete-${entry.id}`}
                      >
                        Delete
                      </button>
                    </div>
                    <p className="whitespace-pre-wrap break-words text-sm text-reef-100">
                      {entry.body}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
